import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { AnimatePresence, motion } from 'framer-motion';
import { X, User, Lock, Mail, Eye, EyeOff, LogIn, UserPlus } from 'lucide-react';
import { useTheme } from '../context/ThemeContext';
import supabaseService from '../services/supabaseService';
import pointsService from '../services/pointsService';

const Overlay = styled(motion.div)`
  position: fixed;
  inset: 0;
  background: rgba(0,0,0,0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
`;

const Modal = styled(motion.div)`
  width: 420px;
  background: ${p => p.theme.mode === 'dark' ? 'rgba(17,18,23,0.7)' : 'rgba(255,255,255,0.7)'};
  backdrop-filter: saturate(180%) blur(18px);
  -webkit-backdrop-filter: saturate(180%) blur(18px);
  border: 1px solid ${p => p.theme.mode === 'dark' ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.06)'};
  border-radius: 16px;
  padding: 24px;
  box-shadow: ${p => p.theme.shadow.md};
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
`;

const Title = styled.h3`
  margin: 0;
  font-size: 18px;
  font-weight: 600;
  color: ${p => p.theme.mode === 'dark' ? '#f0f6fc' : '#24292e'};
`;

const TabContainer = styled.div`
  display: flex;
  margin-bottom: 20px;
  background: ${p => p.theme.mode === 'dark' ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.05)'};
  border-radius: 8px;
  padding: 4px;
`;

const Tab = styled.button`
  flex: 1;
  padding: 8px 16px;
  border: none;
  background: ${p => p.$active ? (p.theme.mode === 'dark' ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.1)') : 'transparent'};
  color: ${p => p.$active ? (p.theme.mode === 'dark' ? '#f0f6fc' : '#24292e') : (p.theme.mode === 'dark' ? '#8b949e' : '#656d76')};
  border-radius: 6px;
  cursor: pointer;
  font-size: 14px;
  font-weight: 500;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;

  &:hover {
    background: ${p => p.theme.mode === 'dark' ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.08)'};
  }
`;

const FormGroup = styled.div`
  margin-bottom: 16px;
`;

const Label = styled.label`
  display: block;
  margin-bottom: 6px;
  font-size: 14px;
  font-weight: 500;
  color: ${p => p.theme.mode === 'dark' ? '#f0f6fc' : '#24292e'};
`;

const InputContainer = styled.div`
  position: relative;
`;

const Input = styled.input`
  width: 100%;
  padding: 12px 16px;
  padding-right: ${p => p.hasIcon ? '40px' : '16px'};
  border: 1px solid ${p => p.theme.mode === 'dark' ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.1)'};
  border-radius: 8px;
  background: ${p => p.theme.mode === 'dark' ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.02)'};
  color: ${p => p.theme.mode === 'dark' ? '#f0f6fc' : '#24292e'};
  font-size: 14px;
  transition: all 0.2s ease;

  &:focus {
    outline: none;
    border-color: ${p => p.theme.colors.primary};
    box-shadow: 0 0 0 3px ${p => p.theme.colors.primary}20;
  }

  &::placeholder {
    color: ${p => p.theme.mode === 'dark' ? '#8b949e' : '#656d76'};
  }
`;

const IconButton = styled.button`
  position: absolute;
  right: 12px;
  top: 50%;
  transform: translateY(-50%);
  background: none;
  border: none;
  color: ${p => p.theme.mode === 'dark' ? '#8b949e' : '#656d76'};
  cursor: pointer;
  padding: 4px;
  border-radius: 4px;
  transition: color 0.2s ease;

  &:hover {
    color: ${p => p.theme.mode === 'dark' ? '#f0f6fc' : '#24292e'};
  }
`;

const Button = styled.button`
  width: 100%;
  padding: 12px 16px;
  background: ${p => p.theme.colors.primary};
  color: white;
  border: none;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;

  &:hover {
    background: ${p => p.theme.colors.primaryHover || p.theme.colors.primary};
    transform: translateY(-1px);
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
    transform: none;
  }
`;

const GuestInfo = styled.div`
  margin-top: 16px;
  padding: 12px;
  background: ${p => p.theme.mode === 'dark' ? 'rgba(10,132,255,0.1)' : 'rgba(10,132,255,0.05)'};
  border: 1px solid ${p => p.theme.mode === 'dark' ? 'rgba(10,132,255,0.2)' : 'rgba(10,132,255,0.1)'};
  border-radius: 8px;
  font-size: 13px;
  color: ${p => p.theme.mode === 'dark' ? '#64d2ff' : '#0969da'};
`;

const ErrorMessage = styled.div`
  margin-top: 8px;
  padding: 8px 12px;
  background: ${p => p.theme.mode === 'dark' ? 'rgba(239,68,68,0.1)' : 'rgba(239,68,68,0.05)'};
  border: 1px solid ${p => p.theme.mode === 'dark' ? 'rgba(239,68,68,0.2)' : 'rgba(239,68,68,0.1)'};
  border-radius: 6px;
  font-size: 13px;
  color: ${p => p.theme.mode === 'dark' ? '#fca5a5' : '#dc2626'};
`;

const SuccessMessage = styled.div`
  margin-top: 8px;
  padding: 8px 12px;
  background: ${p => p.theme.mode === 'dark' ? 'rgba(34,197,94,0.1)' : 'rgba(34,197,94,0.05)'};
  border: 1px solid ${p => p.theme.mode === 'dark' ? 'rgba(34,197,94,0.2)' : 'rgba(34,197,94,0.1)'};
  border-radius: 6px;
  font-size: 13px;
  color: ${p => p.theme.mode === 'dark' ? '#86efac' : '#16a34a'};
`;

function LoginModal({ isOpen, onClose, onLoggedIn, onLoggedOut, onProfileUpdated }) {
  const { theme } = useTheme();
  const [activeTab, setActiveTab] = useState('login');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const userType = pointsService.getUserType();
  const guestBalance = pointsService.getGuestBalance();

  const mapSupabaseError = (rawMessage) => {
    if (!rawMessage) return '요청 처리 중 오류가 발생했습니다.';
    // JSON 본문 추출 시도
    try {
      const match = rawMessage.match(/\{[\s\S]*\}$/);
      if (match) {
        const obj = JSON.parse(match[0]);
        const code = (obj.error_code || obj.code || '').toString().toLowerCase();
        const msg = (obj.msg || obj.message || '').toString();
        if (code.includes('invalid_credentials') || /invalid login credentials/i.test(msg)) {
          return '이메일 또는 비밀번호가 올바르지 않습니다.';
        }
        if (code.includes('email_not_confirmed') || /email not confirmed/i.test(msg)) {
          return '이메일 인증이 완료되지 않았습니다. 메일함(스팸함 포함)을 확인해 주세요.';
        }
        if (code.includes('rate_limit') || /too many/i.test(msg)) {
          return '요청이 많습니다. 잠시 후 다시 시도해 주세요.';
        }
        if (/password/i.test(msg)) {
          return '비밀번호가 올바르지 않습니다.';
        }
        if (/email/i.test(msg) && /exists|duplicate/i.test(msg)) {
          return '이미 등록된 이메일입니다. 로그인하거나 비밀번호 재설정을 이용해 주세요.';
        }
      }
    } catch {}
    // 일반 패턴 매핑
    if (/invalid_credentials/i.test(rawMessage)) return '이메일 또는 비밀번호가 올바르지 않습니다.';
    if (/email.*confirm/i.test(rawMessage)) return '이메일 인증이 완료되지 않았습니다. 메일함을 확인해 주세요.';
    if (/429|too many/i.test(rawMessage)) return '요청이 많습니다. 잠시 후 다시 시도해 주세요.';
    if (/400/.test(rawMessage)) return '요청이 올바르지 않습니다. 입력값을 확인해 주세요.';
    if (/401/.test(rawMessage)) return '인증에 실패했습니다. 다시 로그인해 주세요.';
    return '처리 중 문제가 발생했습니다. 잠시 후 다시 시도해 주세요.';
  };

  useEffect(() => {
    if (isOpen) {
      setEmail('');
      setPassword('');
      setName('');
      setPhone('');
      setError('');
      setSuccess('');
      setShowPassword(false);
    }
  }, [isOpen, activeTab]);

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setSuccess('');

    try {
      const data = await supabaseService.signIn(email, password);
      if (data) {
        // 로그인 성공 시 사용자 정보 가져오기
        try {
          if (supabaseService.isConfigured()) {
            // 사용자 정보를 Supabase에서 가져오기
            const userData = await supabaseService.getUserData(email);
            console.log('LoginModal - 로그인 후 사용자 데이터:', userData);
            
            if (userData && typeof userData.points === 'number') {
              // localStorage 완전 초기화하고 Supabase 포인트만 사용
              console.log('LoginModal - Supabase 실제 포인트:', userData.points);
              console.log('LoginModal - 기존 localStorage 포인트:', pointsService.getCurrentBalance());
              
              localStorage.setItem('ai_agent_points_v1', String(userData.points));
              localStorage.setItem('ai_agent_user_type_v1', 'logged_in');
              
              // 사용자 계정 정보를 localStorage에 저장
              localStorage.setItem('ai_agent_account', JSON.stringify({
                email: userData.email,
                name: userData.name,
                points: userData.points,
                id: userData.id
              }));
              
              console.log('LoginModal - localStorage 초기화 후:', pointsService.getCurrentBalance());
            } else {
              console.log('LoginModal - Supabase에서 포인트를 가져올 수 없음, 기본값 사용');
              pointsService.setLoggedIn(); // 로그인 상태로 설정
            }
          }
        } catch (dbError) {
          console.error('사용자 정보 로드 실패:', dbError);
        }
        
        setSuccess('로그인 성공!');
        pointsService.setLoggedIn();
        if (typeof onLoggedIn === 'function') onLoggedIn();
        setTimeout(() => {
          onClose();
        }, 1000);
      }
    } catch (err) {
      setError(mapSupabaseError(err.message) || '로그인 중 오류가 발생했습니다.');
    } finally {
      setLoading(false);
    }
  };

  const handleSignUp = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setSuccess('');

    try {
      // Supabase 설정 확인
      if (!supabaseService.isConfigured()) {
        setError('Supabase 설정이 올바르지 않습니다. 관리자에게 문의하세요.');
        return;
      }

      // Supabase Auth로 회원가입 (추가 정보를 metadata에 포함)
      const data = await supabaseService.signUp(email, password, {
        name: name || '',
        phone: phone || ''
      });
      
      if (data) {
        // 회원가입 성공 시 추가 정보를 Supabase에 저장
        if (data?.user) {
          try {
            // 신규 사용자에게 1500포인트만 지급 (게스트 포인트 이전 안함)
            const newUserPoints = 1500;
            
            // Hook에서 이미 기본 사용자가 생성되었으므로 업데이트만 수행
            if (supabaseService.isConfigured()) {
              // 즉시 업데이트 시도
              try {
                // 이름과 전화번호를 직접 업데이트
                await supabaseService.request(`users?email=eq.${encodeURIComponent(email)}`, {
                  method: 'PATCH',
                  body: {
                    name: name || null,
                    phone: phone || null
                  }
                });
                
                console.log('LoginModal - 사용자 추가 정보 업데이트 완료:', { name, phone });
              } catch (error) {
                console.error('LoginModal - 사용자 정보 업데이트 실패:', error);
                
                // 실패하면 잠시 후 다시 시도
                setTimeout(async () => {
                  try {
                    await supabaseService.request(`users?email=eq.${encodeURIComponent(email)}`, {
                      method: 'PATCH',
                      body: {
                        name: name || null,
                        phone: phone || null
                      }
                    });
                    console.log('LoginModal - 재시도 후 사용자 추가 정보 업데이트 완료:', { name, phone });
                  } catch (retryError) {
                    console.error('LoginModal - 재시도 후에도 사용자 정보 업데이트 실패:', retryError);
                  }
                }, 3000);
              }
            }
            
            // 로그인 사용자 포인트 설정
            console.log('LoginModal - 신규 사용자 포인트 설정:', newUserPoints);
            pointsService.setBalance(newUserPoints);
            pointsService.setLoggedIn();
            
            // 즉시 Supabase에서 사용자 정보를 다시 가져와서 동기화
            setTimeout(async () => {
              try {
                const freshUserData = await supabaseService.getUserData(email);
                console.log('LoginModal - 회원가입 후 사용자 데이터 재조회:', freshUserData);
                
                if (freshUserData && typeof freshUserData.points === 'number') {
                  pointsService.setBalance(freshUserData.points);
                  console.log('LoginModal - 회원가입 후 포인트 재동기화:', freshUserData.points);
                }
              } catch (error) {
                console.error('LoginModal - 회원가입 후 사용자 데이터 재조회 실패:', error);
              }
            }, 1000);
            
            if (typeof onLoggedIn === 'function') onLoggedIn();
            
            setSuccess(`회원가입 성공! ${newUserPoints.toLocaleString()}포인트가 지급되었습니다. 이메일을 확인해주세요.`);
            
            // 2초 후 모달 닫기
            setTimeout(() => {
              onClose();
            }, 2000);
            
          } catch (dbError) {
            console.error('사용자 정보 저장 실패:', dbError);
            setError('사용자 정보 저장에 실패했습니다. 다시 시도해주세요.');
          }
        } else {
          setSuccess('회원가입 성공! 이메일을 확인해주세요.');
        }
      }
    } catch (err) {
      setError(mapSupabaseError(err.message) || '회원가입 중 오류가 발생했습니다.');
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    supabaseService.signOut();
    pointsService.setLoggedOut();
    if (typeof onLoggedOut === 'function') onLoggedOut();
    onClose();
  };

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <Overlay
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        onClick={onClose}
      >
        <Modal
          initial={{ scale: 0.9, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          exit={{ scale: 0.9, opacity: 0 }}
          onClick={(e) => e.stopPropagation()}
        >
          <Header>
            <Title>계정 관리</Title>
            <button
              onClick={onClose}
              style={{
                background: 'none',
                border: 'none',
                color: theme.mode === 'dark' ? '#8b949e' : '#656d76',
                cursor: 'pointer',
                padding: '4px',
                borderRadius: '4px'
              }}
            >
              <X size={20} />
            </button>
          </Header>

          {userType === 'guest' ? (
            <>
              <TabContainer>
                <Tab $active={activeTab === 'login'} onClick={() => setActiveTab('login')}>
                  <LogIn size={16} />
                  로그인
                </Tab>
                <Tab $active={activeTab === 'signup'} onClick={() => setActiveTab('signup')}>
                  <UserPlus size={16} />
                  회원가입
                </Tab>
              </TabContainer>

              <form onSubmit={activeTab === 'login' ? handleLogin : handleSignUp}>
                {activeTab === 'signup' && (
                  <FormGroup>
                    <Label>이름</Label>
                    <InputContainer>
                      <Input
                        type="text"
                        value={name}
                        onChange={(e) => setName(e.target.value)}
                        placeholder="이름을 입력하세요"
                        required
                      />
                      <User size={16} style={{ position: 'absolute', right: '12px', top: '50%', transform: 'translateY(-50%)', color: theme.mode === 'dark' ? '#8b949e' : '#656d76' }} />
                    </InputContainer>
                  </FormGroup>
                )}

                <FormGroup>
                  <Label>이메일</Label>
                  <InputContainer>
                    <Input
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder="이메일을 입력하세요"
                      required
                    />
                    <Mail size={16} style={{ position: 'absolute', right: '12px', top: '50%', transform: 'translateY(-50%)', color: theme.mode === 'dark' ? '#8b949e' : '#656d76' }} />
                  </InputContainer>
                </FormGroup>

                <FormGroup>
                  <Label>비밀번호</Label>
                  <InputContainer>
                    <Input
                      type={showPassword ? 'text' : 'password'}
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      placeholder="비밀번호를 입력하세요"
                      required
                    />
                    <IconButton
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                    >
                      {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                    </IconButton>
                  </InputContainer>
                </FormGroup>

                {activeTab === 'signup' && (
                  <FormGroup>
                    <Label>전화번호</Label>
                    <InputContainer>
                      <Input
                        type="tel"
                        value={phone}
                        onChange={(e) => setPhone(e.target.value)}
                        placeholder="전화번호를 입력하세요"
                      />
                      <Lock size={16} style={{ position: 'absolute', right: '12px', top: '50%', transform: 'translateY(-50%)', color: theme.mode === 'dark' ? '#8b949e' : '#656d76' }} />
                    </InputContainer>
                  </FormGroup>
                )}

                {error && <ErrorMessage>{error}</ErrorMessage>}
                {success && <SuccessMessage>{success}</SuccessMessage>}

                <Button type="submit" disabled={loading}>
                  {loading ? '처리 중...' : (activeTab === 'login' ? '로그인' : '회원가입')}
                </Button>
              </form>

              <GuestInfo>
                <strong>게스트 사용자</strong><br />
                현재 {guestBalance.toLocaleString()}포인트를 사용할 수 있습니다.<br />
                로그인하시면 더 많은 기능을 이용하실 수 있습니다.
              </GuestInfo>
            </>
          ) : (
            <ProfileEditor onLogout={handleLogout} onProfileUpdated={onProfileUpdated} />
          )}
        </Modal>
      </Overlay>
    </AnimatePresence>
  );
}

export default LoginModal;

function ProfileEditor({ onLogout, onProfileUpdated }) {
  const { theme } = useTheme();
  const [name, setName] = useState(() => localStorage.getItem('olp_display_name') || '');
  const [avatarChar, setAvatarChar] = useState(() => localStorage.getItem('olp_avatar_char') || '🧠');
  const [avatarColor, setAvatarColor] = useState(() => localStorage.getItem('olp_avatar_color') || '#7C3AED');
  const [saving, setSaving] = useState(false);
  const email = JSON.parse(localStorage.getItem('ai_agent_account')||'{}')?.email || '';

  const avatarChoices = ['🧠','📝','🦊','🐼','🐧','🐰','🐱','🐶','🐨','🐢','🐳','🦄','✨','🎓'];
  const colorChoices = ['#7C3AED','#0A84FF','#30D158','#FF9F0A','#FF375F','#FF6B6B','#5856D6','#34C759'];

  const save = async () => {
    setSaving(true);
    try {
      localStorage.setItem('olp_display_name', name);
      localStorage.setItem('olp_avatar_char', avatarChar);
      localStorage.setItem('olp_avatar_color', avatarColor);
      const merged = { ...(JSON.parse(localStorage.getItem('ai_agent_account')||'{}')), name, avatarChar, avatarColor };
      localStorage.setItem('ai_agent_account', JSON.stringify(merged));
      if (email && supabaseService.isConfigured()) {
        await supabaseService.updateUserProfile(email, { name, avatar_char: avatarChar, avatar_color: avatarColor });
      }
      if (typeof onProfileUpdated === 'function') onProfileUpdated({ name, avatarChar, avatarColor });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div>
      <div style={{ textAlign: 'center', marginBottom: 16 }}>
        <div style={{ width: 56, height: 56, borderRadius: 28, background: avatarColor, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', fontSize: 28 }}>{avatarChar}</div>
        <div style={{ marginTop: 8, fontSize: 13, color: theme.mode === 'dark' ? '#8b949e' : '#656d76' }}>{email}</div>
      </div>

      <FormGroup>
        <Label>표시 이름</Label>
        <InputContainer>
          <Input type="text" value={name} onChange={e => setName(e.target.value)} placeholder="이름" />
        </InputContainer>
      </FormGroup>

      <FormGroup>
        <Label>아바타 아이콘</Label>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 6 }}>
          {avatarChoices.map(ch => (
            <button key={ch} onClick={() => setAvatarChar(ch)} style={{ padding: 6, borderRadius: 8, border: ch===avatarChar? '2px solid #0A84FF':'1px solid rgba(0,0,0,0.1)', background: 'transparent', cursor: 'pointer', fontSize: 18 }}>{ch}</button>
          ))}
        </div>
      </FormGroup>

      <FormGroup>
        <Label>아바타 색상</Label>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(8, 1fr)', gap: 6 }}>
          {colorChoices.map(c => (
            <button key={c} onClick={() => setAvatarColor(c)} title={c} style={{ width: 28, height: 28, borderRadius: 6, border: c===avatarColor? '2px solid #0A84FF':'1px solid rgba(0,0,0,0.1)', background: c, cursor: 'pointer' }} />
          ))}
        </div>
      </FormGroup>

      <div style={{ display: 'flex', gap: 8 }}>
        <Button onClick={save} disabled={saving}>{saving ? '저장 중...' : '저장'}</Button>
        <Button onClick={onLogout}>
          <LogIn size={16} />
          로그아웃
        </Button>
      </div>
    </div>
  );
}